import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Badge, Card, PageHeading, Stack, Table } from "~/components";
import { queryClient } from "~/lib/queryClient";
import { formatDateTime } from "~/lib/dates";
import {
  documentUploadsQueryOptions,
  droppedDocuments,
  leafDocuments,
  uploadStatus,
  uploadStatusVariant,
  type DocumentUpload,
} from "~/lib/documentUploads";

export const Route = createFileRoute("/_app/document-uploads/")({
  component: DocumentUploadsIndexPage,
  loader: () => queryClient.ensureQueryData(documentUploadsQueryOptions()),
});

const heading = (
  <PageHeading
    title="Document Uploads"
    subtitle="Each upload runs the same workflow machinery as a case — open one to see its tasks and clear any that need a human."
  />
);

function DocumentUploadsIndexPage() {
  const { data, isPending } = useQuery(documentUploadsQueryOptions());
  const uploads = data?._embedded ?? [];

  if (isPending) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p>Loading uploads…</p>
        </Card>
      </Stack>
    );
  }

  if (uploads.length === 0) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p>No document uploads yet.</p>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {heading}
      <Card>
        <Table>
          <thead>
            <tr>
              <th>Upload</th>
              <th>Uploaded</th>
              <th>Documents</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map((upload) => (
              <UploadRow key={upload.id} upload={upload} />
            ))}
          </tbody>
        </Table>
      </Card>
    </Stack>
  );
}

function UploadRow({ upload }: { upload: DocumentUpload }) {
  const label = upload.name || upload.path || "(untitled)";
  const documents = droppedDocuments(upload);
  const status = uploadStatus(leafDocuments(upload));
  return (
    <tr>
      <td>
        <Link to="/document-uploads/$id" params={{ id: upload.id }}>
          {label}
        </Link>
      </td>
      <td>{formatDateTime(upload.created_at)}</td>
      <td>{documents.length}</td>
      <td>
        <Badge variant={uploadStatusVariant(status)}>{status}</Badge>
      </td>
    </tr>
  );
}
